import React, {
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react'
import { useMutation, useQuery } from '@apollo/react-hooks'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import classNames from 'classnames'
import ReactSVG from 'react-svg'

import REAL_TIME_IN_APP_MESSAGES_QUERY from '../../../../graphql/queries/real-time-in-app-messages.gql'
import DISMISS_REAL_TIME_IN_APP_MESSAGE_MUTATION from '../../../../graphql/mutations/dismiss-real-time-in-app-message.gql'
import RECORD_IN_APP_MESSAGE_EVENT_MUTATION from '../../../../graphql/mutations/record-in-app-message-event.gql'
import { useIsAuthenticated } from '../../../hooks/use-is-authenticated'
import { useIsPageVisible } from '../../../hooks/use-is-page-visible'
import { useIsKid } from '../../../hooks/use-is-kid'
import useSearchParams from '../../../layouts/hooks/useSearchParams'
import { IN_APP_MESSAGES } from '../../../constants'
import { canSelectMessageCta, useSelectMessageCta } from '../hooks/use-select-message-cta'
import { usePollInterval } from '../hooks/use-poll-interval'
import { usePolling } from '../hooks/use-polling'
import Button from '../../../components/button'
import closeIcon from '../../../../images/closeModal.svg'
import notificationIcon from '../../../../images/notification.svg'
import styles from './real-time-in-app-messages.css'

/**
 * Real-time in-app messages: a toast shown over the Home page.
 *
 * Polling lifecycle
 * - The query only runs while the user is signed in, the page is Home ('/')
 *   and not showing search results ('/?search='), the app is in the
 *   foreground (Page Visibility API), maintenance mode is off and the current
 *   profile is not a kid profile. If any condition stops holding, the query
 *   is skipped and polling stops.
 * - When all conditions hold again (e.g. returning to Home or to the tab),
 *   the query fetches immediately (network-only), then polls every
 *   IN_APP_MESSAGE_REAL_TIME_POLLING_SECONDS from app config (0 or not
 *   configured disables scheduled polling).
 * - The toast shows exactly what the latest response returns (the latest eligible one),
 *   An empty array is a successful response meaning no message is currently eligible.
 *   A failed poll hides the toast until the next successful poll.
 *
 * Message interactions
 * - IMPRESSION is recorded once per message ID after it is rendered, not on every poll.
 * - Dismiss hides the toast immediately, then calls dismissRealTimeInAppMessage
 *   and, if Home is still active, refetches to pick up any newer message. If the dismiss
 *   fails, the message stays hidden for this session; the server still has it,
 *   so it comes back on the next app load.
 * - The CTA (VIEW_CONTENT) marks the message read when it is still UNREAD,
 *   records CTA_SELECTED and navigates to the promoted Series or Title.
 */
const RealTimeInAppMessages = () => {
  const { pathname } = useLocation()
  const isAuthenticated = useIsAuthenticated()
  const isPageVisible = useIsPageVisible()
  const isKidProfile = useIsKid()
  // Home with ?search= shows search results instead of the Home page (see MainLayout)
  const { hasSearchParams: isSearchResultPage } = useSearchParams()
  const maintenanceMode = useSelector(state => state.maintenance.mode)
  const searchIsClicked = useSelector(state => state.navigation.searchIsClicked)
  const pollInterval = usePollInterval(IN_APP_MESSAGES.REAL_TIME_POLL_INTERVAL)
  const shouldSkipQuery = !isAuthenticated ||
    !isPageVisible ||
    maintenanceMode ||
    isKidProfile ||
    pathname !== '/' ||
    isSearchResultPage
  const { data, error, refetch } = useQuery(REAL_TIME_IN_APP_MESSAGES_QUERY, {
    fetchPolicy: 'network-only',
    skip: shouldSkipQuery
  })
  usePolling(refetch, pollInterval, !shouldSkipQuery)
  // read by async callbacks (e.g. after dismiss) that must not fetch once the
  // customer has left Home or the app is in the background
  const isQueryActive = useRef(!shouldSkipQuery)
  isQueryActive.current = !shouldSkipQuery
  const [dismissRealTimeInAppMessage] = useMutation(
    DISMISS_REAL_TIME_IN_APP_MESSAGE_MUTATION
  )
  const [recordInAppMessageEvent] = useMutation(
    RECORD_IN_APP_MESSAGE_EVENT_MUTATION
  )
  const selectMessageCta = useSelectMessageCta()
  const recordedImpressionIds = useRef(new Set())
  const [dismissedIds, setDismissedIds] = useState(() => new Set())

  const recordMessageEvent = useCallback(
    (messageId, eventType) => recordInAppMessageEvent({
      variables: {
        input: { messageId, eventType }
      }
    }),
    [recordInAppMessageEvent]
  )

  const dismissMessage = useCallback(id => {
    // hide immediately so the toast doesn't wait for the mutation + refetch,
    // and a second click can't send another dismiss
    setDismissedIds(ids => new Set(ids).add(id))
    dismissRealTimeInAppMessage({ variables: { id } })
      .then(() => {
        // refetch only while Home is still active, to pick up any newer message
        if (isQueryActive.current) return refetch()
        return null
      })
      .catch(() => null)
  }, [dismissRealTimeInAppMessage, refetch])

  const messages = data?.realTimeInAppMessages ?? []
  const visibleMessages = messages.filter(({ id }) => !dismissedIds.has(id))

  useEffect(() => {
    if (shouldSkipQuery || error) return

    messages.forEach(message => {
      if (recordedImpressionIds.current.has(message.id)) return

      recordedImpressionIds.current.add(message.id)
      recordMessageEvent(message.id, 'IMPRESSION').catch(() => {
        recordedImpressionIds.current.delete(message.id)
      })
    })
  }, [error, messages, recordMessageEvent, shouldSkipQuery])

  if (shouldSkipQuery || error || !visibleMessages.length) return null

  return (
    <div className={classNames(styles.stack, {
      [styles.searchIsClicked]: searchIsClicked
    })}
    >
      {visibleMessages.map(message => {
        const {
          id,
          header,
          message: body,
          ctaText,
          startsAt,
          startsAtText,
          thumbnail
        } = message

        return (
          <div className={styles.item} key={id}>
            <div
              className={styles.card}
              data-lbx-e2e={`in-app-message-${id}`}
            >
              <div className={styles.content}>
                <div className={styles.metadata}>
                  {startsAtText && (
                    <>
                      <ReactSVG
                        src={notificationIcon}
                        wrapper="span"
                        beforeInjection={svg => {
                          svg.setAttribute('class', styles.notificationIcon)
                        }}
                      />
                      <time className={styles.timestamp} dateTime={startsAt}>
                        {startsAtText}
                      </time>
                    </>
                  )}
                  <button
                    type="button"
                    aria-label="Dismiss notification"
                    data-lbx-e2e={`in-app-message-dismiss-${id}`}
                    className={styles.closeButton}
                    onClick={() => dismissMessage(id)}
                  >
                    <ReactSVG
                      src={closeIcon}
                      wrapper="span"
                      beforeInjection={svg => {
                        svg.setAttribute('class', styles.closeIcon)
                      }}
                    />
                  </button>
                </div>
                {header && <p className={styles.header}>{header}</p>}
                {body && <p className={styles.body}>{body}</p>}
                {thumbnail && (
                  <img
                    key={thumbnail}
                    className={styles.thumbnail}
                    src={thumbnail}
                    alt=""
                    loading="lazy"
                    onError={e => { e.currentTarget.hidden = true }}
                  />
                )}
                {canSelectMessageCta(message) && (
                  <Button
                    wrapperClassName={styles.ctaButtonWrapper}
                    e2eIdentifier={`in-app-message-cta-${id}`}
                    onClick={() => selectMessageCta(message)}
                    text={ctaText}
                  />
                )}
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default RealTimeInAppMessages
